import { get } from '@ember/object';
import moment from 'moment-timezone';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util (pure) — «UI nuova» title of a fiscal document that has
 *              NO number yet (pro forma / draft): «Fattura senza numero del
 *              11/08/2026» instead of the model label «# 11/08/2026» (M11 live fix 4)
 *   extends:   -
 *   template:  none
 *   exports:   documentTypeName (tenant name of the type, capitalised),
 *              untitledDocumentTitle (the sentence, or null when the record has a
 *              number / is not a fiscal document)
 *   uses:      @ember/object get (records and plain payload rows alike), moment
 *   siblings:  invoice-channel.js
 *   tests:     medical acceptance «m11 live fixes» (apps/beebeeboard-medical)
 *   gotchas:   nuova only: the classic UI keeps the model `label` («#<number>
 *              <date>»). Callers: invoice-detail-nuova (title / summary),
 *              bb-tab-label-nuova (open-record tab), socket-tracker (browser
 *              tab title when the UI is nuova). Returns null for anything that is
 *              not an invoice record (no `document_type`) so a generic caller can
 *              fall back to its own label.
 */

// Fallback words when the organization has no name for the type (tenant names win): translation keys.
const TYPE_NAME = {
  invoice: 'nv.invoice.detail.type_invoice',
  receipt: 'nv.invoice.detail.type_receipt',
  estimate: 'nv.invoice.detail.type_estimate',
  autofattura: 'nv.invoice.detail.type_autofattura'
};
const capitalize = text => {
  const s = String(text ?? '').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

/**
 * Tenant name of the document's type («Fattura», «Ricevuta», «Nota di credito»…), capitalised.
 * @param {object} doc - invoice record or plain row
 * @param {object} intl - intl service
 * @param {object} [organization] - session organization (record or POJO)
 */
function documentTypeName(doc, intl, organization) {
  if (!doc) return '';
  if (get(doc, 'is_creditnote')) return capitalize(intl.t('credit note'));
  const type = get(doc, 'document_type');
  const name = organization ? get(organization, `${type}_name`) : null;
  if (typeof name === 'string' && name.trim()) return capitalize(name);
  return TYPE_NAME[type] ? intl.t(TYPE_NAME[type]) : '';
}

/**
 * «<Tipo> senza numero del <data>» for an invoice record without a number, else null.
 * @param {object} doc - invoice record or plain row
 * @param {object} intl - intl service
 * @param {object} [organization] - session organization
 * @param {string} [name] - the document's name when the caller already has it
 */
function untitledDocumentTitle(doc, intl, organization, name) {
  if (!doc || !intl) return null;
  const type = get(doc, 'document_type');
  if (!type) return null;
  const number = get(doc, 'number');
  if (number !== null && number !== undefined && String(number).trim() !== '') return null;
  const docName = name || documentTypeName(doc, intl, organization);
  if (!docName) return null;
  const raw = get(doc, 'date');
  const date = raw ? moment(raw) : null;
  if (!date?.isValid()) return intl.t('nv.invoice.detail.title_no_number', {
    name: docName
  });
  return intl.t('nv.invoice.detail.title_no_number_date', {
    name: docName,
    date: date.format('DD/MM/YYYY')
  });
}

export { documentTypeName, untitledDocumentTitle };
//# sourceMappingURL=nv-document-title.js.map
